'use client';

import {
    dehydrate,
    hydrate,
    type DehydratedState,
    type QueryClient,
    type Query,
} from '@tanstack/react-query';
import { useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { createLogger } from '../utils/logger';
import {
    clearPersistedQueryCache,
    QUERY_CACHE_STORAGE_PREFIX,
} from '../utils/queryCacheStorage';

const logger = createLogger('QueryPersistence');

export { clearPersistedQueryCache };

/**
 * Bump when the shape of anything on the allowlist changes incompatibly.
 * A mismatch discards the stored cache instead of hydrating stale shapes.
 */
const CACHE_VERSION = 'v1';

const STORAGE_PREFIX = QUERY_CACHE_STORAGE_PREFIX;

/** Cached data older than this is discarded rather than shown. */
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

/** Debounce writes — the cache changes on every query transition. */
const WRITE_DEBOUNCE_MS = 1000;

/**
 * Query-key roots that are safe to persist to localStorage.
 *
 * This is an ALLOWLIST, not a denylist, and that is deliberate. Most OrderUp
 * queries resolve to model CLASS instances (`Tenant`, `Restaurant`, `Order`, …)
 * whose behaviour lives in getters and methods. A JSON round-trip silently
 * degrades those to plain objects — the data still "looks right" in the
 * devtools while every getter reads `undefined`. Persisting broadly would trade
 * a visible slow boot for an invisible correctness bug, so only queries whose
 * payload is plain server JSON by construction are eligible.
 *
 * `meRestaurants` qualifies: it is the unwrapped `/users/me/locations`
 * envelope, typed as a readonly interface and never passed through a model
 * class. It is also precisely the query that gates first paint — restoring it
 * lets a returning user's shell render from cache while the network
 * revalidates in the background.
 *
 * Auth state is excluded on purpose and must stay excluded: the auth snapshot
 * holds a live Firebase `User` instance (not serialisable), and session
 * validation is a security decision that has to be re-made against the server
 * on every load, never restored from disk.
 */
const PERSISTABLE_QUERY_ROOTS: ReadonlySet<string> = new Set([
    'meRestaurants',
]);

interface PersistedEnvelope {
    readonly version: string;
    readonly savedAt: number;
    readonly uid: string;
    readonly state: DehydratedState;
}

const storageKeyFor = (uid: string): string => `${STORAGE_PREFIX}:${CACHE_VERSION}:${uid}`;

function isPersistable(query: Query): boolean {
    if (query.state.status !== 'success') return false;
    const root = Array.isArray(query.queryKey) ? query.queryKey[0] : undefined;
    return typeof root === 'string' && PERSISTABLE_QUERY_ROOTS.has(root);
}

/**
 * Restores the allowlisted slice of a previous session's query cache, and keeps
 * it up to date thereafter.
 *
 * Effect on boot: a returning user's shell renders from cache on the first
 * frame and the network call becomes a background revalidation, rather than a
 * blocking round-trip in front of first paint.
 *
 * The cache is keyed by uid, so signing in as a different user on a shared
 * browser reads a different (usually absent) blob — one user's data can never
 * be hydrated into another's session.
 */
export function usePersistedQueryCache(queryClient: QueryClient): void {
    const { data: authData } = useAuth();
    const { authUser, authResolved } = authData;
    const uid = authUser?.uid ?? null;
    const isAnonymous = authUser?.isAnonymous ?? false;

    useEffect(() => {
        if (typeof window === 'undefined') return;
        // Wait for a definitive answer — hydrating before Firebase reports who
        // the user is could restore the previous account's blob.
        if (!authResolved) return;

        // Signed out (or anonymous): nothing of ours should be sitting on disk.
        if (!uid || isAnonymous) {
            clearPersistedQueryCache();
            return;
        }

        const storageKey = storageKeyFor(uid);

        // ── Restore ──────────────────────────────────────────────────────────
        try {
            const raw = window.localStorage.getItem(storageKey);
            if (raw) {
                const envelope = JSON.parse(raw) as PersistedEnvelope;
                const expired = Date.now() - envelope.savedAt > MAX_AGE_MS;
                if (envelope.version !== CACHE_VERSION || envelope.uid !== uid || expired) {
                    window.localStorage.removeItem(storageKey);
                } else {
                    hydrate(queryClient, envelope.state);
                    logger.debug('Restored persisted query cache', { uid });
                }
            }
        } catch (error) {
            logger.warn('Failed to restore persisted query cache — discarding', {
                error: error instanceof Error ? error.message : String(error),
            });
            try {
                window.localStorage.removeItem(storageKey);
            } catch {
                // ignore
            }
        }

        // ── Persist on change ────────────────────────────────────────────────
        let timer: ReturnType<typeof setTimeout> | null = null;

        const flush = () => {
            try {
                const state = dehydrate(queryClient, { shouldDehydrateQuery: isPersistable });
                const envelope: PersistedEnvelope = {
                    version: CACHE_VERSION,
                    savedAt: Date.now(),
                    uid,
                    state,
                };
                window.localStorage.setItem(storageKey, JSON.stringify(envelope));
            } catch (error) {
                // Quota exceeded / privacy mode / unserialisable payload. Losing
                // the cache costs a slower boot, never correctness.
                logger.warn('Failed to persist query cache', {
                    error: error instanceof Error ? error.message : String(error),
                });
            }
        };

        const unsubscribe = queryClient.getQueryCache().subscribe(() => {
            if (timer) clearTimeout(timer);
            timer = setTimeout(flush, WRITE_DEBOUNCE_MS);
        });

        return () => {
            unsubscribe();
            if (timer) clearTimeout(timer);
        };
    }, [queryClient, uid, isAnonymous, authResolved]);
}
